interface StepSuccessProps {
  phoneNumber: string;
  onDone: () => void;
}

export default function StepSuccess({ phoneNumber, onDone }: StepSuccessProps) {
  return (
    <div className="min-h-full bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center py-8 px-4">
      <div className="max-w-lg w-full">
        {/* Success Card */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
          {/* Animated Checkmark */}
          <div className="mb-6 flex justify-center">
            <div className="relative">
              {/* Outer circle animation */}
              <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center animate-scale-in">
                {/* Inner circle */}
                <div className="w-24 h-24 bg-green-600 rounded-full flex items-center justify-center animate-scale-in-delayed">
                  {/* Checkmark */}
                  <svg
                    className="w-16 h-16 text-white animate-check-in"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Success Message */}
          <h1 className="text-4xl font-black text-gray-900 mb-3">
            Application Received!
          </h1>

          <div className="space-y-4 mb-8">
            <p className="text-lg text-gray-700">
              We have sent a confirmation text to
            </p>
            <div className="inline-block bg-gray-100 px-6 py-2 rounded-lg">
              <p className="text-xl font-bold text-gray-900">{phoneNumber}</p>
            </div>
            <p className="text-base text-gray-600 max-w-md mx-auto">
              Expect to hear from us within <span className="font-bold text-gray-900">24 hours</span>.
              We're excited to review your application!
            </p>
          </div>

          {/* What's Next Section */}
          <div className="bg-blue-50 rounded-2xl p-6 mb-8 text-left">
            <h3 className="font-bold text-gray-900 mb-3 flex items-center">
              <svg
                className="w-5 h-5 text-blue-600 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              What's Next?
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">✓</span>
                <span>Our team will review your application and video</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">✓</span>
                <span>You'll receive a text with next steps</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">✓</span>
                <span>Check your email for additional information</span>
              </li>
            </ul>
          </div>

          {/* Done Button */}
          <button
            onClick={onDone}
            className="w-full h-14 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold text-lg rounded-xl transition-all shadow-lg active:scale-95"
          >
            Done
          </button>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes checkIn {
          0% {
            stroke-dasharray: 0, 100;
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          100% {
            stroke-dasharray: 100, 0;
            opacity: 1;
          }
        }

        .animate-scale-in {
          animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
        }

        .animate-scale-in-delayed {
          animation: scaleIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s forwards;
          opacity: 0;
        }

        .animate-check-in {
          animation: checkIn 0.6s ease-out 0.5s forwards;
          stroke-dasharray: 0, 100;
          opacity: 0;
        }
      `}</style>
    </div>
  );
}

